/**
 * Shared TanStack-Form field primitives.
 *
 * Composable, typed field wrappers built on the InputGroup / Field / RadioGroup
 * / Switch primitives. Each binds a TanStack `AnyFieldApi` to a control and
 * renders inline validation via `FieldError`. Features compose these instead of
 * re-wiring `field.state` boilerplate per form.
 */
import type { ReactNode } from "react";
import type { AnyFieldApi } from "@tanstack/react-form";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupTextarea,
  InputGroupFloatingLabel,
  InputGroupText,
} from "./input-group";
import { FieldError } from "./field";
import { RadioGroup } from "./radio-group";
import { RadioItem } from "./radio-item";
import { Label } from "./label";
import { Switch } from "./switch";

function hasMessage(value: unknown): value is { readonly message: unknown } {
  return typeof value === "object" && value !== null && "message" in value;
}

/**
 * Extract a stable display string from a TanStack Form field error value.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const message = getFieldErrorMessage({ message: "Pflichtfeld" });
 * ```
 */
export function getFieldErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    return error;
  }

  if (hasMessage(error)) {
    return String(error.message);
  }

  return "Unbekannter Formularfehler";
}

function fieldTestId(
  prefix: string,
  fieldName: string,
  control: string,
): string {
  return `${prefix}-${fieldName.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}-${control}`;
}

/**
 * Props for {@link TextField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: TextFieldProps = { field, label: "Name" };
 * ```
 */
export interface TextFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;
  type?: "text" | "email" | "tel" | "url";
  testIdPrefix?: string;
  "data-testid"?: string;
}

/**
 * Single-line text field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="name">{(field) => <TextField field={field} label="Name" required />}</form.Field>
 * ```
 */
export function TextField({
  field,
  label,
  icon,
  required,
  helpText,
  placeholder,
  disabled,
  type = "text",
  testIdPrefix,
  "data-testid": dataTestId,
}: TextFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <InputGroup>
        {icon && (
          <InputGroupAddon align="inline-start">
            <InputGroupText>{icon}</InputGroupText>
          </InputGroupAddon>
        )}
        <InputGroupInput
          id={id}
          type={type}
          placeholder={placeholder}
          value={field.state.value || ""}
          onChange={(e) => field.handleChange(e.target.value)}
          disabled={disabled}
          required={required}
          aria-invalid={field.state.meta.errors.length > 0}
          data-testid={
            dataTestId ??
            (testIdPrefix
              ? fieldTestId(testIdPrefix, field.name, "input")
              : undefined)
          }
        />
        <InputGroupFloatingLabel htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </InputGroupFloatingLabel>
      </InputGroup>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}

/**
 * Props for {@link NumberField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: NumberFieldProps = { field, label: "Dauer" };
 * ```
 */
export interface NumberFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  "data-testid"?: string;
}

/**
 * Numeric field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="duration">{(field) => <NumberField field={field} label="Dauer" min={0} />}</form.Field>
 * ```
 */
export function NumberField({
  field,
  label,
  icon,
  required,
  helpText,
  placeholder,
  disabled,
  min,
  max,
  step = 1,
  "data-testid": dataTestId,
}: NumberFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <InputGroup>
        {icon && (
          <InputGroupAddon align="inline-start">
            <InputGroupText>{icon}</InputGroupText>
          </InputGroupAddon>
        )}
        <InputGroupInput
          id={id}
          type="number"
          placeholder={placeholder}
          value={field.state.value ?? ""}
          onChange={(e) =>
            field.handleChange(
              e.target.value ? parseFloat(e.target.value) : undefined,
            )
          }
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          step={step}
          aria-invalid={field.state.meta.errors.length > 0}
          data-testid={dataTestId}
        />
        <InputGroupFloatingLabel htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </InputGroupFloatingLabel>
      </InputGroup>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}

/**
 * Props for {@link DateField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: DateFieldProps = { field, label: "Datum" };
 * ```
 */
export interface DateFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  required?: boolean;
  helpText?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

/**
 * Date field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="date">{(field) => <DateField field={field} label="Datum" />}</form.Field>
 * ```
 */
export function DateField({
  field,
  label,
  icon,
  required,
  helpText,
  disabled,
  "data-testid": dataTestId,
}: DateFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <InputGroup>
        {icon && (
          <InputGroupAddon align="inline-start">
            <InputGroupText>{icon}</InputGroupText>
          </InputGroupAddon>
        )}
        <InputGroupInput
          id={id}
          type="date"
          value={field.state.value || ""}
          onChange={(e) => field.handleChange(e.target.value)}
          disabled={disabled}
          required={required}
          aria-invalid={field.state.meta.errors.length > 0}
          data-testid={dataTestId}
        />
        <InputGroupFloatingLabel htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </InputGroupFloatingLabel>
      </InputGroup>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}

/**
 * Props for {@link TextareaField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: TextareaFieldProps = { field, label: "Notiz" };
 * ```
 */
export interface TextareaFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  testIdPrefix?: string;
  "data-testid"?: string;
}

/**
 * Multi-line text field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="notes">{(field) => <TextareaField field={field} label="Notiz" />}</form.Field>
 * ```
 */
export function TextareaField({
  field,
  label,
  icon,
  required,
  helpText,
  placeholder,
  disabled,
  rows = 4,
  testIdPrefix,
  "data-testid": dataTestId,
}: TextareaFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <InputGroup>
        <InputGroupAddon align="block-start">
          <InputGroupText>
            {icon}
            <span>
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </span>
          </InputGroupText>
        </InputGroupAddon>
        <InputGroupTextarea
          id={id}
          aria-label={label}
          placeholder={placeholder}
          value={field.state.value || ""}
          onChange={(e) => field.handleChange(e.target.value)}
          disabled={disabled}
          required={required}
          rows={rows}
          aria-invalid={field.state.meta.errors.length > 0}
          data-testid={
            dataTestId ??
            (testIdPrefix
              ? fieldTestId(testIdPrefix, field.name, "textarea")
              : undefined)
          }
        />
      </InputGroup>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}

/**
 * Option for {@link RadioField}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const option: RadioFieldOption = { value: "a", label: "A" };
 * ```
 */
export interface RadioFieldOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

/**
 * Props for {@link RadioField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: RadioFieldProps = { field, label: "Typ", options: [] };
 * ```
 */
export interface RadioFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  options: RadioFieldOption[];
  required?: boolean;
  helpText?: string;
  disabled?: boolean;
  columns?: 1 | 2 | 3 | 4 | 5;
  testIdPrefix?: string;
}

/**
 * Radio-group field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="type">{(field) => <RadioField field={field} label="Typ" options={opts} />}</form.Field>
 * ```
 */
export function RadioField({
  field,
  label,
  icon,
  options,
  required,
  helpText,
  disabled,
  columns = 2,
  testIdPrefix,
}: RadioFieldProps) {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
    5: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-5",
  };

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        {icon}
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <RadioGroup
        className={`grid ${gridCols[columns]} gap-3`}
        value={field.state.value || ""}
        onValueChange={(value) => field.handleChange(value)}
        disabled={disabled}
        data-testid={
          testIdPrefix
            ? fieldTestId(testIdPrefix, field.name, "radio-group")
            : undefined
        }
      >
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <RadioItem
              key={option.value}
              id={`${field.name}-${option.value}`}
              value={option.value}
              label={option.label}
              title={option.description}
              data-testid={
                testIdPrefix
                  ? fieldTestId(
                      testIdPrefix,
                      `${field.name}-${option.value}`,
                      "radio",
                    )
                  : undefined
              }
            >
              {Icon && <Icon />}
            </RadioItem>
          );
        })}
      </RadioGroup>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}

/**
 * Option for {@link SelectField}.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const option: SelectFieldOption = { value: "a", label: "A" };
 * ```
 */
export interface SelectFieldOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Props for {@link SelectField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: SelectFieldProps = { field, label: "Auswahl", options: [] };
 * ```
 */
export interface SelectFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  options: SelectFieldOption[];
  required?: boolean;
  helpText?: string;
  disabled?: boolean;
  testIdPrefix?: string;
  "data-testid"?: string;
}

/**
 * Native select field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @deprecated Prefer {@link RadioField} for a small number of options.
 * @example
 * ```tsx
 * <form.Field name="kind">{(field) => <SelectField field={field} label="Auswahl" options={opts} />}</form.Field>
 * ```
 */
export function SelectField({
  field,
  label,
  icon,
  options,
  required,
  helpText,
  disabled,
  testIdPrefix = "field",
  "data-testid": dataTestId,
}: SelectFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="flex items-center gap-2">
        {icon}
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <select
        id={id}
        data-testid={
          dataTestId ?? fieldTestId(testIdPrefix, field.name, "select")
        }
        value={field.state.value || ""}
        onChange={(e) => field.handleChange(e.target.value)}
        disabled={disabled}
        required={required}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        aria-invalid={field.state.meta.errors.length > 0}
      >
        <option value="">{label} auswählen…</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
            {option.description && ` - ${option.description}`}
          </option>
        ))}
      </select>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}

/**
 * Props for {@link SwitchField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: SwitchFieldProps = { field, label: "Aktiv" };
 * ```
 */
export interface SwitchFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  helpText?: string;
  disabled?: boolean;
  testIdPrefix?: string;
  "data-testid"?: string;
}

/**
 * Boolean switch field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="active">{(field) => <SwitchField field={field} label="Aktiv" />}</form.Field>
 * ```
 */
export function SwitchField({
  field,
  label,
  icon,
  helpText,
  disabled,
  testIdPrefix = "field",
  "data-testid": dataTestId,
}: SwitchFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2 rounded-md border border-input px-3 py-2">
        {icon}
        <Switch
          id={id}
          data-testid={
            dataTestId ?? fieldTestId(testIdPrefix, field.name, "switch")
          }
          checked={field.state.value || false}
          onCheckedChange={(checked) => field.handleChange(checked)}
          disabled={disabled}
        />
        <Label htmlFor={id} className="cursor-pointer">
          {label}
        </Label>
      </div>
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
    </div>
  );
}

/**
 * Props for {@link CurrencyField}.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * declare const field: import("@tanstack/react-form").AnyFieldApi;
 * const props: CurrencyFieldProps = { field, label: "Betrag" };
 * ```
 */
export interface CurrencyFieldProps {
  field: AnyFieldApi;
  label: string;
  icon?: ReactNode;
  required?: boolean;
  helpText?: string;
  placeholder?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

/**
 * Currency (non-negative, 2-decimal) field bound to a TanStack Form field.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <form.Field name="amount">{(field) => <CurrencyField field={field} label="Betrag" />}</form.Field>
 * ```
 */
export function CurrencyField({
  field,
  label,
  icon,
  required,
  helpText,
  placeholder,
  disabled,
  "data-testid": dataTestId,
}: CurrencyFieldProps) {
  const id = `field-${field.name}`;
  return (
    <div className="space-y-2">
      <InputGroup>
        {icon && (
          <InputGroupAddon align="inline-start">
            <InputGroupText>{icon}</InputGroupText>
          </InputGroupAddon>
        )}
        <InputGroupInput
          id={id}
          type="number"
          placeholder={placeholder}
          value={field.state.value ?? ""}
          onChange={(e) =>
            field.handleChange(
              e.target.value ? parseFloat(e.target.value) : undefined,
            )
          }
          disabled={disabled}
          required={required}
          min={0}
          step={0.01}
          aria-invalid={field.state.meta.errors.length > 0}
          data-testid={dataTestId}
        />
        <InputGroupFloatingLabel htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </InputGroupFloatingLabel>
      </InputGroup>
      {helpText && !field.state.meta.errors.length && (
        <p className="text-xs text-muted-foreground">{helpText}</p>
      )}
      {field.state.meta.errors.length > 0 && (
        <FieldError>
          {getFieldErrorMessage(field.state.meta.errors[0])}
        </FieldError>
      )}
    </div>
  );
}
